import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { Task, CreateTaskInput, TaskStatus } from '@/types/task';
import { useWorkItemsStore } from '@/stores/useWorkItemsStore';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import type { WorkItem } from '@/types/workItem';
import { stripRichTextToPlainText } from '@/lib/messageFormatting';

interface TaskContextType {
  tasks: Task[];
  isTaskPanelOpen: boolean;
  pendingCount: number;
  isLoading: boolean;
  error: string | null;
  selectedTaskId: string | null;
  addTask: (input: CreateTaskInput) => Promise<void>;
  addSimpleTask: (content: string, note?: string) => Promise<void>;
  removeTask: (taskId: string) => Promise<void>;
  updateTaskStatus: (taskId: string, status: TaskStatus) => Promise<void>;
  updateTaskNote: (taskId: string, note: string) => Promise<void>;
  updateTaskMessageContent: (messageId: string, content: string) => void;
  toggleTaskPanel: () => void;
  openTaskPanel: () => void;
  closeTaskPanel: () => void;
  isMessageActioned: (messageId: string) => boolean;
  getTaskForMessage: (messageId: string) => Task | undefined;
  selectTask: (taskId: string | null) => void;
  refreshTasks: () => Promise<void>;
}

const TaskContext = createContext<TaskContextType | undefined>(undefined);

/** Map a WorkItem owned by the current user into the Task shape used by TaskPanel/TaskItem. */
function workItemToTask(item: WorkItem): Task {
  const source = item.sources[0];
  return {
    id: item.id,
    messageId: source?.messageId ?? '',
    messageContent: item.title,
    channelId: source?.channelId ?? '',
    channelName: source?.channelId ? 'source' : 'Personal',
    authorUserId: item.createdByUserId,
    authorUsername: '',
    authorDisplayName: undefined,
    authorAvatarUrl: undefined,
    createdAt: item.createdAt,
    status: (item.status === 'done' || item.status === 'wontdo') ? 'completed' : 'pending',
    note: item.description ?? undefined,
  };
}

export function TaskProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { currentWorkspaceId } = useWorkspace();

  const {
    workItems,
    isLoading,
    error,
    loadWorkItems,
    addWorkItem,
    editWorkItem,
    removeWorkItem,
    reset,
  } = useWorkItemsStore();

  const [isTaskPanelOpen, setIsTaskPanelOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // Derive "my tasks" from the shared work items store — items I own.
  const tasks: Task[] = workItems
    .filter(wi => user && wi.ownerUserId === user.userId)
    .map(workItemToTask);

  // Load work items for this workspace (shared with WorkItemsPage).
  useEffect(() => {
    if (!currentWorkspaceId || !user) {
      reset();
      setSelectedTaskId(null);
      setIsTaskPanelOpen(false);
      return;
    }
    void loadWorkItems();
  }, [currentWorkspaceId, user, loadWorkItems, reset]);

  const pendingCount = tasks.filter(t => t.status !== 'completed').length;

  const refreshTasks = useCallback(async () => {
    await loadWorkItems();
  }, [loadWorkItems]);

  const addTask = useCallback(async (input: CreateTaskInput) => {
    if (!user) return;
    const plain = stripRichTextToPlainText(input.messageContent);
    const title = plain ? (plain.length > 120 ? plain.slice(0, 120) + '…' : plain) : 'Untitled task';

    await addWorkItem({
      title,
      description: input.note,
      ownerUserId: user.userId,
      channelId: input.channelId || undefined,
      visibility: 'personal',
      sourceMessageIds: input.messageId ? [input.messageId] : undefined,
    });

    setIsTaskPanelOpen(true);
  }, [user, addWorkItem]);

  const addSimpleTask = useCallback(async (content: string, note?: string) => {
    if (!user) return;
    await addWorkItem({
      title: content.slice(0, 120),
      description: note,
      ownerUserId: user.userId,
      visibility: 'personal',
    });
  }, [user, addWorkItem]);

  const removeTask = useCallback(async (taskId: string) => {
    await removeWorkItem(taskId);
  }, [removeWorkItem]);

  const updateTaskStatus = useCallback(async (taskId: string, status: TaskStatus) => {
    const workItemStatus = status === 'completed' ? 'done' : 'open';
    await editWorkItem(taskId, { status: workItemStatus });
  }, [editWorkItem]);

  const updateTaskNote = useCallback(async (taskId: string, note: string) => {
    await editWorkItem(taskId, { description: note });
  }, [editWorkItem]);

  // No-op: content is derived from the work item title, not cached separately.
  const updateTaskMessageContent = useCallback((_messageId: string, _content: string) => {}, []);

  const toggleTaskPanel = useCallback(() => setIsTaskPanelOpen(prev => !prev), []);
  const openTaskPanel = useCallback(() => setIsTaskPanelOpen(true), []);
  const closeTaskPanel = useCallback(() => setIsTaskPanelOpen(false), []);

  const selectTask = useCallback((taskId: string | null) => {
    setSelectedTaskId(taskId);
    if (taskId) setIsTaskPanelOpen(true);
  }, []);

  const isMessageActioned = useCallback((messageId: string) => {
    if (!user) return false;
    return workItems.some(
      wi =>
        wi.ownerUserId === user.userId &&
        wi.status !== 'done' &&
        wi.status !== 'wontdo' &&
        wi.sources.some(s => s.messageId === messageId)
    );
  }, [workItems, user]);

  const getTaskForMessage = useCallback((messageId: string): Task | undefined => {
    if (!user) return undefined;
    const item = workItems
      .filter(
        wi =>
          wi.ownerUserId === user.userId &&
          wi.sources.some(s => s.messageId === messageId)
      )
      .sort((a, b) => {
        const aActive = a.status !== 'done' && a.status !== 'wontdo';
        const bActive = b.status !== 'done' && b.status !== 'wontdo';
        if (aActive !== bActive) return aActive ? -1 : 1;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      })[0];
    return item ? workItemToTask(item) : undefined;
  }, [workItems, user]);

  return (
    <TaskContext.Provider value={{
      tasks,
      isTaskPanelOpen,
      pendingCount,
      isLoading,
      error,
      selectedTaskId,
      addTask,
      addSimpleTask,
      removeTask,
      updateTaskStatus,
      updateTaskNote,
      updateTaskMessageContent,
      toggleTaskPanel,
      openTaskPanel,
      closeTaskPanel,
      isMessageActioned,
      getTaskForMessage,
      selectTask,
      refreshTasks,
    }}>
      {children}
    </TaskContext.Provider>
  );
}

export function useTasks() {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error('useTasks must be used within a TaskProvider');
  }
  return context;
}

